import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Calendar,
  Clock,
  DollarSign,
  Edit,
  Building
} from 'lucide-react';
import { useMatterWithClient } from '../hooks/useMatter';
import { usePageNavigation } from '../contexts/NavigationContext';
import DocumentSection from '../components/documents/DocumentSection';

const MatterDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { matter, isLoading, error, loadMatter } = useMatterWithClient();
  const { setPageNavigation } = usePageNavigation();

  useEffect(() => {
    if (id) {
      loadMatter(id);
    }
  }, [id, loadMatter]);

  // Set up navigation breadcrumbs when matter data loads
  useEffect(() => {
    if (matter) {
      setPageNavigation(`${matter.title}`, [
        { label: 'Matters', href: '/matters' },
        { label: matter.title, href: `/matters/${matter.id}` }
      ]);
    } else if (id) {
      // Set loading state breadcrumb
      setPageNavigation('Matter Details', [
        { label: 'Matters', href: '/matters' },
        { label: 'Matter Details' }
      ], true);
    }
  }, [matter, id, setPageNavigation]);

  const handleEdit = () => {
    // TODO: Implement edit functionality
    console.log('Edit matter:', matter);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="max-w-6xl mx-auto">
          {/* Loading Header */}
          <div className="mb-6 animate-pulse">
            <div className="h-6 bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="h-8 bg-gray-700 rounded w-1/2 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/3"></div>
          </div>
          
          {/* Loading Tabs */}
          <div className="border-b border-gray-700 mb-6">
            <div className="flex space-x-8">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-4 bg-gray-700 rounded w-20 mb-4"></div>
              ))}
            </div>
          </div>
          
          {/* Loading Content */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="h-40 bg-gray-700 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-red-900 border border-red-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-300 mb-2">Error Loading Matter</h3>
            <p className="text-red-200">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!matter) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 text-center">
            <FileText className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">Matter Not Found</h3>
            <p className="text-gray-500">The requested matter could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-900 text-green-300 border-green-700';
      case 'completed':
        return 'bg-blue-900 text-blue-300 border-blue-700';
      case 'on-hold':
        return 'bg-yellow-900 text-yellow-300 border-yellow-700';
      case 'cancelled':
        return 'bg-red-900 text-red-300 border-red-700';
      default:
        return 'bg-gray-900 text-gray-300 border-gray-700';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(new Date(date));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Full-width container for desktop-first design */}
      <div className="w-full">
        {/* Compact Header Section - Reduced vertical spacing */}
        <div className="px-6 py-2">
          <div className="flex items-center justify-between">
            {/* Left side: Matter info and details in horizontal layout */}
            <div className="flex-1">
              <div className="flex items-center space-x-6 mb-3">
                <h1 className="text-2xl font-bold text-white">{matter.title}</h1>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadge(matter.status)}`}>
                  {matter.status.charAt(0).toUpperCase() + matter.status.slice(1)}
                </span>
              </div>
              
              {/* Horizontal info cards - compact layout */}
              <div className="flex items-center space-x-8 text-sm">
                {/* Client Information */}
                {matter.client && (
                  <button
                    onClick={() => navigate(`/clients/${matter.client.id}`)}
                    className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors group"
                  >
                    <Building className="w-4 h-4" />
                    <span className="font-medium group-hover:underline">
                      {matter.client.name}
                    </span>
                  </button>
                )}
                
                {/* Matter Type */}
                <span className="flex items-center space-x-2 text-gray-400">
                  <FileText className="w-4 h-4" />
                  <span>{matter.type}</span>
                </span>
                
                {/* Created Date */}
                <span className="flex items-center space-x-2 text-gray-500">
                  <Calendar className="w-4 h-4" />
                  <span>Created {formatDate(matter.dateCreated)}</span>
                </span>
                
                {/* Last Activity */}
                <span className="flex items-center space-x-2 text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>Active {formatDate(matter.lastActivity)}</span>
                </span>
                
                {/* Billed Amount */}
                <span className="flex items-center space-x-2 text-gray-500">
                  <DollarSign className="w-4 h-4" />
                  <span>{formatCurrency(matter.billedAmount)}</span>
                </span>
              </div>
            </div>
            
            {/* Right side: Edit button */}
            <button
              onClick={handleEdit}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Edit className="w-4 h-4" />
              <span>Edit</span>
            </button>
          </div>
          
          {/* Optional description - only show if exists and keep compact */}
          {matter.description && (
            <p className="mt-3 text-gray-300 text-sm leading-relaxed max-w-4xl">{matter.description}</p>
          )}
        </div>

        {/* Documents Section */}
        <div className="mx-6 bg-gray-800 rounded-lg border border-gray-700">
          <DocumentSection matter={matter} onMatterUpdate={loadMatter} />
        </div>
      </div>
    </div>
  );
};

export default MatterDetails;
